import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { initAnalytics, sendPageview } from "@/lib/analytics";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import PublicShell from "@/components/layout/PublicShell";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import DashboardHome from "@/pages/DashboardHome";
import AffiliateModule from "@/pages/AffiliateModule";
import Schedules from "@/pages/Schedules";
import RefreshRadar from "@/pages/RefreshRadar";
import PaapiOperations from "@/pages/PaapiOperations";
import Businesses from "@/pages/Businesses";
import Content from "@/pages/Content";
import Subscriptions from "@/pages/Subscriptions";
import Settings from "@/pages/Settings";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import LandingPage from "@/pages/LandingPage";
import PublicBrowse from "@/pages/PublicBrowse";
import PublicRead from "@/pages/PublicRead";
import PublicAuthor from "@/pages/PublicAuthor";
import PricingPage from "@/pages/PricingPage";
import FeaturesPage from "@/pages/FeaturesPage";
import AdvertisePage from "@/pages/AdvertisePage";
import AdminDashboard from "@/pages/AdminDashboard";
import OwnerDashboard from "@/pages/OwnerDashboard";
import TwitterCallback from "@/pages/TwitterCallback";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          {/* Public site */}
          <Route element={<PublicShell />}>
            <Route index element={<LandingPage />} />
            <Route path="/browse" element={<PublicBrowse />} />
            <Route path="/read/:slug" element={<PublicRead />} />
            <Route path="/author/:slug" element={<PublicAuthor />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
          </Route>

          {/* Admin console (admin + owner) */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Owner console (owner only) */}
          <Route
            path="/owner"
            element={
              <ProtectedRoute>
                <OwnerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Auth flow */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
          <Route
            path="/auth/twitter/callback"
            element={
              <ProtectedRoute>
                <TwitterCallback />
              </ProtectedRoute>
            }
          />

          {/* Protected dashboard */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="affiliate" element={<AffiliateModule />} />
            <Route path="schedules" element={<Schedules />} />
            <Route path="refresh-radar" element={<RefreshRadar />} />
            <Route path="paapi" element={<PaapiOperations />} />
            <Route path="businesses" element={<Businesses />} />
            <Route path="content" element={<Content />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { sendPageview(location.pathname + location.search); }, [location]);
  return null;
}
